import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Crown, Flame, Loader2 } from "lucide-react";
import { BQ } from "../../lib/bharatQuest";

export default function Leaderboard({ meUsername }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    BQ.leaderboard(50).then((d) => { setData(d); setLoading(false); });
  }, []);

  if (loading) return <div className="text-center py-12 text-[#E8EBF0]/55"><Loader2 className="inline animate-spin" /></div>;

  const top3 = data.leaderboard.slice(0, 3);
  const rest = data.leaderboard.slice(3);

  return (
    <div data-testid="bq-leaderboard">
      <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-3">Global Leaderboard</div>
      <h2 className="font-mythos text-3xl md:text-4xl font-extrabold tracking-tight text-[#F4EFE6] max-w-2xl">
        Top <span className="mythos-gold">Cultural Intelligence</span> scores.
      </h2>
      <p className="text-[#E8EBF0]/65 mt-2 max-w-2xl">{data.total_players.toLocaleString()} players competing worldwide.</p>

      {/* Podium */}
      <div className="grid sm:grid-cols-3 gap-4 mt-10" data-testid="bq-podium">
        {[1, 0, 2].map((idx) => {
          const r = top3[idx];
          if (!r) return <div key={idx} />;
          const isFirst = r.rank === 1;
          const colors = ["#C9A66B", "#A8A8A8", "#9A6B3F"];
          return (
            <motion.div
              key={r.username}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className={`vault-card rounded-2xl p-6 text-center relative border-2 ${
                r.username === meUsername ? "border-[#C9A66B]" : "border-[#2A3349]"
              } ${isFirst ? "sm:order-2 sm:scale-105" : idx === 0 ? "sm:order-1" : "sm:order-3"}`}
              data-testid={`bq-podium-rank-${r.rank}`}
            >
              {isFirst && (
                <Crown className="absolute -top-3 left-1/2 -translate-x-1/2 mythos-gold" size={28} fill="currentColor" />
              )}
              <div className="text-5xl font-mythos font-extrabold mb-2" style={{ color: colors[r.rank - 1] }}>
                #{r.rank}
              </div>
              <div className="font-mythos font-bold text-[#F4EFE6] truncate">@{r.username}</div>
              <div className="text-[10px] tracking-[0.18em] uppercase mythos-gold font-bold mt-1">{r.title}</div>
              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                <div className="bg-[#0F1623] rounded-lg p-2">
                  <div className="text-[9px] tracking-[0.18em] uppercase text-[#7d8294] font-bold">CIQ</div>
                  <div className="font-mythos text-[#F4EFE6] font-bold text-lg">{r.ciq}</div>
                </div>
                <div className="bg-[#0F1623] rounded-lg p-2">
                  <div className="text-[9px] tracking-[0.18em] uppercase text-[#7d8294] font-bold">XP</div>
                  <div className="font-mythos text-[#F4EFE6] font-bold text-lg">{r.xp}</div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Rest of leaderboard */}
      {rest.length > 0 && (
        <div className="mt-10 vault-card rounded-2xl overflow-hidden border border-[#2A3349]">
          <table className="w-full text-sm" data-testid="bq-leaderboard-table">
            <thead className="border-b mythos-rule">
              <tr className="text-left text-[10px] tracking-[0.18em] uppercase text-[#7d8294] font-bold">
                <th className="px-5 py-3">Rank</th>
                <th className="px-5 py-3">Player</th>
                <th className="px-5 py-3 hidden sm:table-cell">Title</th>
                <th className="px-5 py-3 text-right">CIQ</th>
                <th className="px-5 py-3 text-right hidden sm:table-cell">Streak</th>
              </tr>
            </thead>
            <tbody>
              {rest.map((r) => (
                <tr key={r.username} className={`border-b border-[#2A3349]/50 transition ${r.username === meUsername ? "bg-[#C9A66B]/10" : "hover:bg-[#1A2436]/50"}`} data-testid={`bq-row-${r.username}`}>
                  <td className="px-5 py-3 font-mythos font-bold text-[#F4EFE6]">#{r.rank}</td>
                  <td className="px-5 py-3 text-[#F4EFE6]">@{r.username} {r.username === meUsername && <span className="text-[10px] mythos-gold font-bold ml-1">YOU</span>}</td>
                  <td className="px-5 py-3 mythos-gold text-xs hidden sm:table-cell">{r.title}</td>
                  <td className="px-5 py-3 text-right font-mono text-[#F4EFE6] font-bold">{r.ciq}</td>
                  <td className="px-5 py-3 text-right text-[#E8EBF0]/65 hidden sm:table-cell">
                    {r.streak > 0 && <span className="inline-flex items-center gap-1"><Flame size={11} className="mythos-gold" />{r.streak}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.leaderboard.length === 0 && (
        <div className="text-center py-16 text-[#E8EBF0]/55" data-testid="bq-leaderboard-empty">No players yet. You could be #1!</div>
      )}
    </div>
  );
}
